import { useState } from 'react';
import {
  Modal, Stack, Group, Text, SegmentedControl, ActionIcon, Divider, Button, Textarea, Tooltip, Badge,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatDate } from '../../lib/format';
import { apiError, gradeColor, type JournalCell } from '../../lib/journal';

const VALUES = ['1', '2', '3', '4', '5'];

interface Props {
  /** `null` — modal yopiq. */
  cell: { studentId: string; fullName: string; date: string; grades: JournalCell[] } | null;
  subjectId: string;
  termId: string;
  onClose: () => void;
  /** Har muvaffaqiyatli o'zgarishdan keyin jurnalni qayta so'rash. */
  onChanged: () => void;
}

/**
 * Jurnal katagi — bitta o'quvchining bitta kundagi baholari.
 * Bir kunda bir nechta baho bo'lishi mumkin (§6.2: `grades[date]` — MASSIV;
 * og'zaki + yozma baho bir kunda qonuniy).
 *
 * Katakda BAHO QIYMATI tahrirlanadi va baho o'chiriladi; mavjud izoh faqat
 * KO'RSATILADI (§6.2 uni javobda beradi). Izohni to'liq tahrirlash «Baholar»
 * sahifasidagi tahrirlash oynasida — bu yerda tasodifan ustidan yozilmasin.
 */
export function GradeCellModal({ cell, subjectId, termId, onClose, onChanged }: Props) {
  const { t } = useTranslation();
  const [newValue, setNewValue] = useState('5');
  const [comment, setComment] = useState('');

  const patchMut = useMutation({
    mutationFn: ({ id, value }: { id: string; value: number }) => api.patch(`/grades/${id}`, { value }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('gradesPage.editSaved') }); onChanged(); },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/grades/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('gradesPage.deleted') }); onChanged(); },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  const addMut = useMutation({
    mutationFn: () =>
      api.post('/grades', {
        studentId: cell!.studentId,
        subjectId,
        // Chorak topilmagan bo'lsa yuborilmaydi — backend uni `date` dan aniqlaydi (§2.2).
        ...(termId ? { termId } : {}),
        value: Number(newValue),
        comment: comment || undefined,
        date: cell!.date,
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('gradesPage.gradeAdded') });
      setComment('');
      setNewValue('5');
      onChanged();
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  return (
    <Modal
      opened={!!cell}
      onClose={onClose}
      centered
      title={cell ? `${cell.fullName} · ${formatDate(cell.date)}` : ''}
    >
      {cell && (
        <Stack>
          {!cell.grades.length ? (
            <Text c="dimmed" fz="sm" lh={1.5}>{t('journal.cellEmpty')}</Text>
          ) : (
            <Stack gap="xs">
              {cell.grades.map((g) => (
                <Group key={g.id} justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
                  <Stack gap={4}>
                    <Group gap="xs" wrap="nowrap">
                      <Badge size="lg" color={gradeColor(g.value)}>{g.value}</Badge>
                      <SegmentedControl
                        size="xs"
                        data={VALUES}
                        value={String(g.value)}
                        onChange={(v) => patchMut.mutate({ id: g.id, value: Number(v) })}
                        disabled={patchMut.isPending || delMut.isPending}
                      />
                    </Group>
                    {/* Mavjud izoh — faqat ko'rsatiladi, bu yerda tahrirlanmaydi */}
                    {g.comment && <Text c="dimmed" fz="xs" lh={1.5}>{g.comment}</Text>}
                  </Stack>
                  <Tooltip label={t('gradesPage.deleteGrade')}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('gradesPage.deleteGrade')}
                      loading={delMut.isPending && delMut.variables === g.id}
                      onClick={() => delMut.mutate(g.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </Stack>
          )}

          <Divider label={t('journal.addGrade')} labelPosition="center" />

          <SegmentedControl fullWidth data={VALUES} value={newValue} onChange={setNewValue} />
          <Textarea
            label={t('gradesPage.comment')}
            autosize
            minRows={2}
            value={comment}
            // `e.currentTarget` UPDATER ICHIDA O'QILMAYDI — qiymat oldindan olinadi.
            onChange={(e) => { const v = e.currentTarget.value; setComment(v); }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button leftSection={<IconPlus size={16} />} loading={addMut.isPending} onClick={() => addMut.mutate()}>
              {t('journal.addGrade')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
